import type { VariableEntry } from "@/schemas/workflow/notification-node";
import type { VariableSelectionData } from "../components/variable-selector";
import { NodeTypes } from "@/workflow_api/types/workflow";

interface UseTemplateVariableInputProps {
  elements: VariableEntry[];
  onChange: (elements: VariableEntry[]) => void;
}

export const useTemplateVariableInput = ({
  elements,
  onChange,
}: UseTemplateVariableInputProps) => {
  // Para inputs directos (type="inp"), solo recibe el valor string
  // Para variables (type="var"), recibe los datos completos con connection/field o global_type
  const updateElement = (id: string, data: string | VariableSelectionData) => {
    const index = parseInt(id);
    const newElements = [...elements];
    if (typeof data === "string") {
      // Input directo: solo actualizar value, limpiar connection, field y global_type
      newElements[index] = {
        ...newElements[index],
        value: data,
        connection: undefined,
        field: undefined,
        global_type: undefined,
      };
    } else if (data.global_type) {
      // Variable global: usar value y global_type, sin connection ni field
      newElements[index] = {
        ...newElements[index],
        value: data.value,
        connection: undefined,
        field: undefined,
        global_type: data.global_type,
      };
    } else {
      if (
        data.ref_name === NodeTypes.N8N ||
        data.ref_name === NodeTypes.INTEROPERABILITY
      ) {
        // Variable normal: actualizar value, connection y field
        newElements[index] = {
          ...newElements[index],
          value: data.value,
          connection: data.connection,
          output_name: data.value,
          field: undefined,
          global_type: undefined,
        };
      } else {
        // Variable normal: actualizar value, connection y field
        newElements[index] = {
          ...newElements[index],
          value: data.value,
          connection: data.connection,
          field: data.field,
          global_type: undefined,
          ...(data.output_name ? { output_name: data.output_name } : {}),
        };
      }
    }
    onChange(newElements);
  };

  const removeElement = (id: string) => {
    const index = parseInt(id);
    const newElements = elements.filter((_, i) => i !== index);
    onChange(newElements);
  };

  const addElement = () => {
    onChange([
      ...elements,
      {
        type: "inp",
        value: "",
        var_type: elements[0]?.var_type || "STRING",
      },
    ]);
  };

  const toggleElementType = (id: string) => {
    const index = parseInt(id);
    const newElements = [...elements];
    const currentType = newElements[index].type;
    newElements[index] = {
      type: currentType === "inp" ? "var" : "inp",
      value: "",
      var_type: newElements[index].var_type,
      // Limpiar connection, field y global_type al cambiar de tipo
      connection: undefined,
      field: undefined,
      global_type: undefined,
    };
    onChange(newElements);
  };

  const canAddMoreElements = elements.every((el) => el.value !== "");

  return {
    updateElement,
    removeElement,
    addElement,
    toggleElementType,
    canAddMoreElements,
  };
};
